import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "column q-gutter-sm" }
const _hoisted_2 = { class: "row items-center q-gutter-sm" }
const _hoisted_3 = { class: "font-text-m" }
const _hoisted_4 = { class: "text-weight-medium" }
const _hoisted_5 = { class: "folder-picker__content relative-position" }
const _hoisted_6 = { class: "folder-picker__list" }
const _hoisted_7 = { class: "column col" }
const _hoisted_8 = ["disabled", "onClick"]
const _hoisted_9 = {
  key: 0,
  class: "folder-picker__hint"
}
const _hoisted_10 = { class: "folder-picker__hint" }
const _hoisted_11 = {
  key: 0,
  class: "folder-picker__hint folder-picker__hint--current"
}
const _hoisted_12 = { class: "row justify-end q-gutter-sm s-mt-m" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import SModal from 'SharedComponents/ui/Modal/SModal.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import useCustomLoading from 'SharedDir/common/composables/useCustomLoading';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { ServiceKey } from 'SharedDir/constants/file-system';
import { Folder } from 'SharedDir/declarations/filesystem';
import { resolveService } from 'SharedDir/utils/vue';


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'FolderPickerDialog' },
  __name: 'FolderPickerDialog',
  props: /*@__PURE__*/_mergeModels({
    rootFolderId: {},
    title: {},
    excludeFolderIds: { default: () => [] },
    allowedEntity: { default: null },
    allowedEntityId: { default: null }
  }, {
    "modelValue": { type: Boolean, ...{ required: true } },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["select"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {



const props = __props;

const visible = _useModel<boolean>(__props, "modelValue");

const emits = __emit;

const { t } = useI18n();
const { loading, requestOptions } = useCustomLoading();
const FS = resolveService(ServiceKey, undefined, true);

const currentDirectoryId = ref(props.rootFolderId);
const parents = ref<Record<number, number | null>>({ [props.rootFolderId]: null });
const currentDirectory = ref<Folder | null>(null);

const excludedIds = computed(() => new Set(props.excludeFolderIds));
const hasEntityScope = computed(
  () => props.allowedEntity != null && props.allowedEntityId != null
);

// Устаревшие ветки (isOutdated) — не цель для move/copy; пользователь их не выбирает.
const childFolders = computed(() => {
  if (!currentDirectory.value) {
    return [];
  }
  return currentDirectory.value.children.filter((folder) => !folder.isOutdated);
});

function isForeignEntity(folder: Folder | null) {
  if (!folder || !hasEntityScope.value) {
    return false;
  }
  return folder.entity !== props.allowedEntity || folder.entityId !== props.allowedEntityId;
}

function isFolderDisabled(folder: Folder | null) {
  if (!folder) {
    return true;
  }
  return folder.isReadonly || excludedIds.value.has(folder.id) || isForeignEntity(folder);
}

function disableReason(folder: Folder | null): string | null {
  if (!folder) {
    return null;
  }
  if (isForeignEntity(folder)) {
    return t('documents.folderPicker.foreignEntityHint');
  }
  if (folder.isReadonly) {
    return t('documents.folderPicker.readonlyHint');
  }
  if (excludedIds.value.has(folder.id)) {
    return t('documents.folderPicker.excludedHint');
  }
  return null;
}

async function loadDirectory(folderId: number) {
  // Пока ls() в полёте — показываем пустой список под оверлеем, а не содержимое прошлой папки.
  currentDirectory.value = null;
  currentDirectory.value = await makeRequest(() => FS.ls(folderId), requestOptions);
  currentDirectoryId.value = folderId;
}

async function openFolder(folderId: number) {
  // В чужую сущность не заходим — только смотрим как disabled sibling.
  const folder = childFolders.value.find((item) => item.id === folderId) ?? null;
  if (loading.value || isFolderDisabled(folder)) {
    return;
  }
  // Свой стек «Назад»: API отдаёт только children, без breadcrumbs.
  if (typeof parents.value[folderId] === 'undefined') {
    parents.value[folderId] = currentDirectoryId.value;
  }
  await loadDirectory(folderId);
}

async function goToParent() {
  if (loading.value) {
    return;
  }
  const parentId = parents.value[currentDirectoryId.value];
  if (parentId !== undefined && parentId !== null) {
    await loadDirectory(parentId);
  }
}

function selectFolder(folderId: number) {
  if (loading.value) {
    return;
  }
  if (isFolderDisabled(currentDirectory.value) && folderId === currentDirectoryId.value) {
    return;
  }
  if (excludedIds.value.has(folderId)) {
    return;
  }
  if (folderId === currentDirectoryId.value && isForeignEntity(currentDirectory.value)) {
    return;
  }
  const child = childFolders.value.find((item) => item.id === folderId);
  if (child && isForeignEntity(child)) {
    return;
  }
  emits('select', folderId);
  close();
}

function close() {
  visible.value = false;
}

watch(visible, async (isVisible) => {
  if (!isVisible) {
    return;
  }
  currentDirectoryId.value = props.rootFolderId;
  parents.value = { [props.rootFolderId]: null };
  await loadDirectory(props.rootFolderId);
});

return (_ctx: any,_cache: any) => {
  const _component_s_btn = _resolveComponent("s-btn")!
  const _component_q_spinner_dots = _resolveComponent("q-spinner-dots")!
  const _component_q_inner_loading = _resolveComponent("q-inner-loading")!

  return (_openBlock(), _createBlock(SModal, {
    modelValue: visible.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((visible).value = $event)),
    title: _ctx.title,
    size: "middle",
    onClose: close
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('documents.folderPicker.current')) + ":", 1),
          _createElementVNode("span", _hoisted_4, _toDisplayString(currentDirectory.value?.name), 1)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("ul", _hoisted_6, [
            (currentDirectoryId.value !== _ctx.rootFolderId)
              ? (_openBlock(), _createElementBlock("li", {
                  key: 0,
                  class: "folder-picker__item row items-center q-gutter-sm",
                  onClick: goToParent
                }, [
                  _createVNode(SIcon, {
                    size: "20px",
                    name: "app:arrow-normal-top"
                  }),
                  _createElementVNode("span", null, _toDisplayString(_unref(t)('documents.folderPicker.back')), 1)
                ]))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(childFolders.value, (folder) => {
              return (_openBlock(), _createElementBlock("li", {
                key: folder.id,
                class: _normalizeClass(["folder-picker__item row items-center q-gutter-sm", { 'folder-picker__item--disabled': isFolderDisabled(folder) }])
              }, [
                _createVNode(SIcon, { name: "custom:files/folder" }),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "folder-picker__open",
                    disabled: isFolderDisabled(folder),
                    onClick: ($event: any) => (openFolder(folder.id))
                  }, _toDisplayString(folder.name), 9, _hoisted_8),
                  (disableReason(folder))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(disableReason(folder)), 1))
                    : _createCommentVNode("", true)
                ]),
                (!isFolderDisabled(folder))
                  ? (_openBlock(), _createBlock(_component_s_btn, {
                      key: 0,
                      dense: "",
                      flat: "",
                      "no-caps": "",
                      type: "secondary",
                      disable: _unref(loading),
                      label: _unref(t)('documents.folderPicker.selectHere'),
                      onClick: ($event: any) => (selectFolder(folder.id))
                    }, null, 8, ["disable", "label", "onClick"]))
                  : _createCommentVNode("", true)
              ], 2))
            }), 128))
          ]),
          _createVNode(_component_q_inner_loading, {
            showing: _unref(loading),
            class: "folder-picker__loading"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_spinner_dots, {
                size: "40px",
                color: "brand"
              }),
              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)('documents.folderPicker.loading')), 1)
            ]),
            _: 1
          }, 8, ["showing"])
        ]),
        (disableReason(currentDirectory.value))
          ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(disableReason(currentDirectory.value)), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_component_s_btn, {
            "no-caps": "",
            type: "secondary",
            label: _unref(t)('documents.folderPicker.cancel'),
            onClick: close
          }, null, 8, ["label"]),
          _createVNode(_component_s_btn, {
            "no-caps": "",
            type: "brand",
            disable: 
            _unref(loading) || isFolderDisabled(currentDirectory.value) || excludedIds.value.has(currentDirectoryId.value)
          ,
            label: _unref(t)('documents.folderPicker.selectCurrent'),
            onClick: _cache[0] || (_cache[0] = ($event: any) => (selectFolder(currentDirectoryId.value)))
          }, null, 8, ["disable", "label"])
        ])
      ])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})